import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import emailjs from '@emailjs/browser';



type SubmitStatus = 'idle' | 'sending' | 'success' | 'error';
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = '6zJSINF9Gd2fGIHVF';
// Validation schema
const validationSchema = Yup.object({
  firstName: Yup.string()
    .required('First Name is required')
    .min(2, 'Too short')
    .max(50, 'Too long'),
  lastName: Yup.string()
    .required('Last Name is required')
    .min(2, 'Too short')
    .max(50, 'Too long'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  website: Yup.string().url('Invalid URL').optional(),
  message: Yup.string()
    .min(10, 'Message too short')
    .max(1000, 'Message too long'),
});

const Contact: React.FC = () => {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      website: '',
      message: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Log payload
      console.log('Form Payload:', values);

      //Send mail
      setStatus('sending');

      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            name: `${values.firstName} ${values.lastName}`,
            email: values.email,
            website: values.website,
            message: values.message
          },
          EMAILJS_PUBLIC_KEY
        );
        setStatus('success');

        toast.success('Thank you! Your message has been sent.', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        resetForm();
      } catch (error) {
        console.error('EmailJS error:', error);
        setStatus('error');

        toast.error('Something went wrong. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    },
  });

  return (
    <>
      {/* Toast Container - Required */}
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
      />

      <div className='min-h-screen bg-[#FFFDF8] py-12 px-4'>
        <div className='max-w-4xl mx-auto'>
          {/* Title */}
          <div
            className='max-w-7xl mx-auto text-center mb-12'
            style={{ paddingTop: '6rem' }}
          >
            <h1
              className='text-[48px] text-[#021847] mb-4 text-left'
              style={{ fontFamily: 'Roboto Serif' }}
            >
              Contact Us
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={formik.handleSubmit} className='space-y-8'>
            {/* Name Fields */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* First Name */}
              <div>
                <input
                  type='text'
                  name='firstName'
                  placeholder='First Name *'
                  className={`w-full px-4 py-3 text-gray-700 bg-[#F8F5EF] border rounded-md focus:outline-none focus:ring-2 transition-all ${formik.touched.firstName && formik.errors.firstName
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                    }`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <p className='mt-1 text-sm text-red-600'>
                    {formik.errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <input
                  type='text'
                  name='lastName'
                  placeholder='Last Name *'
                  className={`w-full px-4 py-3 text-gray-700 bg-[#F8F5EF] border rounded-md focus:outline-none focus:ring-2 transition-all ${formik.touched.lastName && formik.errors.lastName
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                    }`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <p className='mt-1 text-sm text-red-600'>
                    {formik.errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Email & Website */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Email */}
              <div>
                <input
                  type='email'
                  name='email'
                  placeholder='Email *'
                  className={`w-full px-4 py-3 text-gray-700 bg-[#F8F5EF] border rounded-md focus:outline-none focus:ring-2 transition-all ${formik.touched.email && formik.errors.email
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                    }`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className='mt-1 text-sm text-red-600'>
                    {formik.errors.email}
                  </p>
                )}
              </div>

              {/* Website */}
              <div>
                <input
                  type='text'
                  name='website'
                  placeholder='Website'
                  className='w-full px-4 py-3 text-gray-700 bg-[#F8F5EF] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.website}
                />
                {formik.touched.website && formik.errors.website && (
                  <p className='mt-1 text-sm text-red-600'>
                    {formik.errors.website}
                  </p>
                )}
              </div>
            </div>

            {/* Message */}
            <div>
              <textarea
                name='message'
                placeholder='Message'
                rows={6}
                className={`w-full px-4 py-3 text-gray-700 bg-[#F8F5EF] border rounded-md focus:outline-none focus:ring-2 transition-all resize-none ${formik.touched.message && formik.errors.message
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                  }`}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.message}
              />
              {formik.touched.message && formik.errors.message && (
                <p className='mt-1 text-sm text-red-600'>
                  {formik.errors.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={formik.isSubmitting}
                className='px-8 py-3 bg-[#1D1D1D] text-white font-medium rounded-md hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed'
              >
                CONTACT US
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Contact;